import React, { useState, useContext, useEffect, useRef } from "react";
import { AuthContext } from "../App";
import { useLocation } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  BookOpen, ChevronDown, ChevronRight, ShieldCheck, Users, Eye, Hammer,
  CircleUserRound, Lock, Check, X, Download, Loader2,
} from "lucide-react";
import { Button } from "./ui/button";
import { DefectsFlowchart } from "./help/DefectsFlowchart";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IMG = (name) => `/manual/${name}?v=6`;

// ---- Roles ----
const ROLES = [
  { key: "admin", label: "Platform Admin", icon: ShieldCheck, color: "bg-rose-600", blurb: "The operator. Full control across every company and site." },
  { key: "company_owner", label: "Company Owner", icon: ShieldCheck, color: "bg-indigo-600", blurb: "Runs their own company — all their sites, their team, billing." },
  { key: "site_manager", label: "Site Manager", icon: Hammer, color: "bg-amber-600", blurb: "Runs day-to-day work — but only on the sites assigned to them." },
  { key: "supervisor", label: "Supervisor", icon: CircleUserRound, color: "bg-sky-600", blurb: "Internal oversight. Mostly read-only, but can manage defects." },
  { key: "view_only", label: "View Only", icon: Eye, color: "bg-slate-500", blurb: "Read-only access to assigned sites. Cannot change anything." },
];
const ROLE_LABEL = { admin: "Platform Admin", company_owner: "Company Owner", site_manager: "Site Manager", supervisor: "Supervisor", view_only: "View Only" };

// ---- Per-section access rules ----
const ALL = ["admin", "company_owner", "site_manager", "supervisor", "view_only"];
const PLATFORM_ONLY = ["demo-admin", "subscriptions", "admin-companies", "platform-branding"]; // only the Platform Admin
// Who can SEE a section (default = everyone). Admin always sees all.
const VIEW = {
  tasks: ["admin", "company_owner", "site_manager", "supervisor"],
  expenses: ["admin", "company_owner", "site_manager"],
  "site-photos": ["admin", "company_owner", "site_manager"],
  sites: ["admin", "company_owner", "supervisor"],
  "site-managers": ["admin", "company_owner", "supervisor"],
  "manage-lists": ["admin", "company_owner", "supervisor"],
  "company-profile": ["admin", "company_owner"],
  "demo-admin": ["admin"],
  subscriptions: ["admin"],
  // Company Accounts sections — Owner + Platform Admin only
  "running-company": ["admin", "company_owner"],
  "manage-team": ["admin", "company_owner"],
  "head-office-rollup": ["admin", "company_owner"],
};
// Who can EDIT / take actions on a section (drives the "Full access / View only" badge).
const EDIT = {
  dashboard: ["admin", "company_owner", "site_manager"],
  calendar: ["admin", "company_owner", "site_manager", "supervisor"],
  planner: ["admin", "company_owner", "site_manager"],
  tasks: ["admin", "company_owner", "site_manager"],
  phone: ["admin", "company_owner", "site_manager"],
  "site-photos": ["admin", "company_owner", "site_manager"],
  defects: ["admin", "company_owner", "site_manager", "supervisor"],
  clients: ["admin", "company_owner", "site_manager"],
  "ohs-home": ["admin", "company_owner", "site_manager"],
  "ohs-attendance": ["admin", "company_owner", "site_manager"],
  "ohs-inductions": ["admin", "company_owner", "site_manager"],
  "ohs-safety-walks": ["admin", "company_owner", "site_manager"],
  "ohs-toolbox-talks": ["admin", "company_owner", "site_manager"],
  expenses: ["admin", "company_owner", "site_manager"],
  sites: ["admin", "company_owner"],
  "site-managers": ["admin", "company_owner"],
  "manage-lists": ["admin", "company_owner"],
  "company-profile": ["admin", "company_owner"],
  "running-company": ["admin", "company_owner"],
  "manage-team": ["admin", "company_owner"],
  "head-office-rollup": ["admin", "company_owner"],
  "demo-admin": ["admin"],
  subscriptions: ["admin"],
  "platform-branding": ["admin"],
};
const INFO_SECTIONS = ["login", "home", "support"]; // no access badge needed
const canView = (id, role) => {
  if (role === "admin") return true;
  if (role === "company_owner") return !PLATFORM_ONLY.includes(id);
  return (VIEW[id] || ALL).includes(role);
};
const canEdit = (id, role) => {
  if (role === "company_owner") return !PLATFORM_ONLY.includes(id);
  return (EDIT[id] || []).includes(role);
};

const Y = "yes", N = "no", P = "part";
// Columns: [label, admin, company_owner, site_manager, supervisor, view_only, note]
const PERMISSIONS = [
  ["See all sites", Y, P, N, N, N, "Company Owner = all sites in your company. Others = assigned sites only."],
  ["Create / edit daily diary entries", Y, Y, Y, N, N, "Owner across their company; Site Managers on their sites only."],
  ["Create / edit Tasks", Y, Y, Y, N, N, "View-Only users don't even see the Tasks/All-Tasks panel."],
  ["Create / produce Expense Claims", Y, Y, Y, N, N, "Site Managers see only their OWN claims; Owners can view ALL their managers' claims (view only)."],
  ["Create / edit Defects (incl. close-out)", Y, Y, Y, Y, N, "Supervisors CAN manage defects (the one exception to their read-only access)."],
  ["Upload / tag Site Photos", Y, Y, Y, N, N, "Supervisor & View-Only have no access to Site Photos at all."],
  ["Record OH&S attendance / inductions / safety walks / toolbox talks", Y, Y, Y, N, N, "Scoped to assigned sites for Site Managers. Anyone except View-Only can sign a toolbox talk; only Owner/Admin can reopen a locked one."],
  ["Client Comms, Variations & Photo updates", Y, Y, Y, N, N, "Send links to clients, log variations, share photo updates."],
  ["Create new sites", Y, Y, N, N, N, "Company Owner only. Site Managers can no longer create sites."],
  ["Edit site details", Y, Y, Y, N, N, "Site Managers may edit their assigned sites."],
  ["Manage Team (create staff, assign sites)", Y, Y, N, N, N, "You manage Site Manager / Supervisor / View-Only within your own company."],
  ["Deactivate / reactivate users", Y, Y, N, N, N, "Owner across their own team; a company's last active Owner can't be deactivated."],
  ["Head-Office Overview (roll-up)", Y, Y, N, N, N, "Covers all sites in your company."],
  ["Manage Lists (trades, companies)", Y, Y, N, N, N, "Each person keeps their own private lists."],
  ["Manage own billing / subscription", Y, Y, N, N, N, "Company-level billing belongs to the Owner."],
  ["Suspend / lock a company", Y, N, N, N, N, "Platform Admin only — blocks login + public links until reactivated."],
  ["Backup / Purge / Restore company data", Y, N, N, N, N, "Platform Admin only — purge auto-takes a backup first and emails the link."],
  ["Manage multiple company owners", Y, N, N, N, N, "Platform Admin only — at least one active owner is always required."],
  ["Platform: Demo & Trials / all Subscribers / Visitor stats", Y, N, N, N, N, "Platform Admin only (operator)."],
  ["Print / export the User Manual (PDF)", Y, N, N, N, N, "Platform Admin ONLY — controlled distribution (Admin or Company edition)."],
];

function PermCell({ v }) {
  if (v === Y) return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-700"><Check className="w-4 h-4" /></span>;
  if (v === N) return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-rose-100 text-rose-600"><X className="w-4 h-4" /></span>;
  return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">~</span>;
}

const GROUPS = [
  {
    group: "Getting Started",
    sections: [
      {
        id: "login", title: "Login & Roles", img: "login.png",
        intro: "Everyone signs in from this screen with an email and password. There is no public self-registration — accounts are created by your Company Owner. The free-trial 'Start Free Trial' panel lets prospective customers request a demo.",
        elements: [
          "Email + password → 'Sign In to Dashboard'.",
          "What you see after login depends entirely on your role (see your access summary above).",
          "'Start Free Trial' panel: requests a 30-day demo (one site, no credit card); an Admin approves it.",
        ],
        gating: "Anyone with an account can log in. The account's role decides which menus and buttons appear.",
      },
      {
        id: "home", title: "Home & Navigation", img: "home.png",
        intro: "After login you land on Home — a set of large cards (Site, Defect Management, Client Management, OH & S, and Controls). The same areas are reachable from the 'Menu' button (top-right dropdown).",
        elements: [
          "Click a card to open that area; 'Site' and 'Controls' expand into sub-cards.",
          "The top bar shows the logo, your name + role, and the Menu dropdown.",
          "Menu items and cards are filtered to your role — you only ever see what you're allowed to use.",
        ],
        gating: "Site, Defects, Clients and OH&S show for all logged-in roles. 'Controls' appears for Company Owner (and Supervisor). Tasks and Site Photos are hidden from View-Only.",
      },
      {
        id: "support", title: "Contact Support", img: "support.png",
        intro: "Reach the Site Pro App team for help. Open it any time from the Menu → Contact Support (available to every role).",
        elements: [
          "Company Owners & Admins see full support details: support email, phone and hours.",
          "Site Managers, Supervisors and View-Only see a lightweight 'email us and we'll call you back' option (no direct phone).",
          "Support contact details are set by the platform operator under Platform Branding.",
        ],
        gating: "Available to all roles. The amount of contact detail shown is tiered by role (owners/admin get phone + hours).",
      },
    ],
  },
  {
    group: "Site",
    sections: [
      {
        id: "dashboard", title: "Dashboard — Daily Site Diary", img: "dashboard.png",
        intro: "The Dashboard is the daily site diary for a chosen Project (site) and date. It records weather, who was on site, deliveries, equipment, events and a to-do list for that day, and is the source of the printable Daily Report.",
        elements: [
          "Pick the PROJECT (site) and DATE at the top — everything below is for that day.",
          "Sections: Weather, No-Shows, Work Details/Contractors, On-Site Equipment, Materials Received, Day Labour, Visitors, Significant Events.",
          "'Site Diary Photos' card (bottom) shows photos marked 'Attach to diary' for that site+date — view only.",
          "Save Daily Record stores the day; Print PDF generates the Daily Report (diary photos are appended with capture times).",
        ],
        gating: "Company Owner & Site Manager can create/edit (Site Manager on assigned sites only). Supervisor & View-Only can view but not edit.",
      },
      {
        id: "calendar", title: "Calendar View", img: "calendar.png",
        intro: "A month grid for one selected site, showing daily activity at a glance plus an 'All Tasks' panel. This is the screen where day numbers, colours and dots all carry meaning — explained below.",
        elements: [
          "DATE NUMBER (top of each cell): the day of the month. Today's number is shown in amber.",
          "DL:n (blue) = number of Day Labour on site that day. C:n (green) = number of Contractor personnel that day. Shown only when there were people on site.",
          "ORANGE DOT (•): the day has INCOMPLETE tasks. It disappears once every task for that day is completed. Hover shows how many.",
          "CELL BACKGROUND — colour legend: amber = today; blue = a 'lost day' (rain/weather); grey/faded = a non-working day (weekend, or a weekday marked as a public holiday — excluded from working-day calculations); blue outline = the day you've selected.",
          "SMALL SQUARE (top-right corner): the non-working-day toggle. White = working day; filled dark = non-working/excluded. Click it to flag a public holiday (weekday) or a 'bonus catch-up day' (weekend). Weekends are always excluded from calculations even when shown as working.",
          "Clicking anywhere in a day cell opens the Dashboard diary for that date.",
          "ALL TASKS panel (below): tasks grouped by site, with Status (default 'Incomplete') and Priority filters. Priority badge colours: red = High, yellow = Medium, grey = Low. Rows turn green when complete and red when overdue ('Overdue!'). Tick the box to complete; pencil = edit, bin = delete.",
        ],
        gating: "Company Owner sees all your company's sites; Site Manager/Supervisor/View-Only see their assigned sites. View-Only does NOT see the day toggle squares or the All-Tasks panel and cannot edit tasks. Marking non-working days requires Company Owner, Site Manager or Supervisor.",
      },
      {
        id: "planner", title: "Look-Forward Planner (Gantt)", img: "planner.png",
        intro: "A horizontal Gantt timeline for planning activities forward across a site — trades, durations and sequencing shown as coloured bars across dates.",
        elements: [
          "Each row is an activity/trade; the bar spans its start→end dates along the timeline header.",
          "Scroll the timeline to move through the date range; bars are colour-coded by activity.",
          "Add, edit and re-schedule activities to build the forward programme; export/print where available.",
        ],
        gating: "Company Owner & Site Manager can create/edit the programme (Site Manager on assigned sites). Supervisor & View-Only can view only.",
      },
      {
        id: "tasks", title: "Tasks", img: "tasks.png",
        intro: "Create and track action items for a site, each with a due date, priority, optional project grouping and assignees. Tasks also surface on the Calendar (orange dot + All-Tasks panel).",
        elements: [
          "Each task: title, description, scheduled date, priority (High/Medium/Low), status (Incomplete / In Progress / Complete), optional Project and assignees.",
          "Overdue incomplete tasks are flagged; completing a task removes the calendar's orange dot for that day once all are done.",
          "Assign tasks to one or more site managers; filter by status/priority.",
        ],
        gating: "Company Owner & Site Manager can create/edit/complete/delete tasks. Supervisor can view. View-Only cannot see Tasks at all (hidden from menu and cards).",
      },
      {
        id: "expenses", title: "Expense Claims", img: "expenses.png",
        intro: "A Site Manager's personal out-of-pocket reimbursement record. Add each purchase with its receipt, preview, then 'Produce' to lock the claim and generate one clean PDF for your accounts team. There's no approval or payment inside the app — it simply replaces the paper claim form.",
        elements: [
          "Start a claim for a site, then add LINE ITEMS — date, store, description, optional invoice #, amount, and a receipt (photo or PDF, up to 10 MB each).",
          "Edit or remove items freely while the claim is a draft; the running TOTAL sums automatically.",
          "PREVIEW & PRODUCE: review the generated PDF, keep editing if needed, then Produce to LOCK it (sequential ID e.g. EXP-0014). No line items can be added after lock — forgot one? Start a new claim.",
          "The PDF compiles a summary form plus every receipt (images embedded and any PDF receipts' pages merged in) as the following pages.",
          "Two personal tick-boxes — 'Submitted to accounts' and 'Payment received' — stay toggleable forever; they're your own tracking, not an approval step.",
        ],
        gating: "Site Manager & Company Owner create / edit / produce. A Site Manager sees ONLY their own claims; a Company Owner can view ALL their site managers' claims (view only). Supervisor & View-Only have no access.",
      },
      {
        id: "reports", title: "Reports & Analytics", img: "reports.png",
        intro: "Aggregated statistics for a site/date range — working days, lost days, personnel totals and trends — with charts and PDF export.",
        elements: [
          "Overview stats: working days, lost days (weather), total personnel, contractor vs day-labour split, etc.",
          "Calculations exclude non-working days (weekends/public holidays) flagged on the Calendar.",
          "Charts visualise attendance/activity over time; export to PDF for sharing.",
        ],
        gating: "All roles can view reports for the sites they can access (Company Owner = all your company's sites). Data comes from diary entries created by Company Owner/Site Managers.",
      },
      {
        id: "contractor", title: "Contractor View — Attendance", img: "contractor.png",
        intro: "A multi-site, multi-contractor attendance board showing, across a rolling 3-month window, which contractors were on site each day and how many people they had. Built from the daily diary's contractor entries.",
        elements: [
          "SELECT SITES and SELECT CONTRACTORS (multi-select). Each chosen contractor gets a unique colour in the legend, with its total personnel across the months shown.",
          "Three months are shown side by side (previous, current, next); use ‹ › to move the window.",
          "Inside each day cell: one small COLOURED SQUARE per contractor (matching the legend colour) followed by a NUMBER = how many of that contractor's people were on site that day.",
          "NO SHOW: if a contractor was expected but didn't attend, its square has a RED RING and shows 'NS' instead of a number.",
          "Each contractor keeps the same row position every day (blank when not present) so you can scan one contractor across the month easily.",
          "Today's cell has a blue outline. Click any day to open that day's diary record. 'Print Attendance Report' produces a PDF.",
        ],
        gating: "Company Owner sees all your company's sites; Site Manager/Supervisor/View-Only are limited to assigned sites. Viewing is open to those roles; attendance is entered by Company Owner/Site Managers in the daily diary.",
      },
      {
        id: "phone", title: "Phone Records", img: "phone-records.png",
        intro: "A log of telephone discussions per site — who called, when, what was discussed and any follow-up — creating a written record of verbal instructions.",
        elements: [
          "Record caller, date/time, subject and notes for each call against a site.",
          "Searchable history; useful as evidence of verbal instructions or agreements.",
        ],
        gating: "Company Owner & Site Manager can add/edit entries (Site Manager on assigned sites). Supervisor & View-Only can view.",
      },
      {
        id: "site-photos", title: "Site Photos", img: "site-photos.png",
        intro: "A general site photo record (progress & proof shots) separate from Defects and the Diary. One gallery per site, with two independent flags per photo: visible-to-client and attach-to-diary.",
        elements: [
          "GALLERY: photos grouped by date, with Area / date-range / 'client-visible only' filters. Blue eye badge = visible to client; green 'Diary' tag = attached to that day's diary.",
          "ADD PHOTOS: capture/upload several at once, then tag each photo individually (area, attach-to-diary, visible-to-client) before uploading.",
          "CLIENT UPDATES tab: client-visible photos for a day auto-group into an update you can Send to the client via a secure link (they view + acknowledge, no login). Diary photos are internal and never shared with clients.",
          "Diary-tagged photos also appear on the Dashboard for that day and are embedded (with capture times) in the Daily PDF.",
        ],
        gating: "Company Owner & Site Manager only (Site Manager on assigned sites). Supervisor & View-Only have NO access. Photos are strictly isolated per site — no manager can see another site's photos.",
      },
    ],
  },
  {
    group: "Defects",
    sections: [
      {
        id: "defects", title: "Defects Management", img: "defects.png",
        intro: "Track building defects per site and dwelling/lot through to close-out, with photos, responsible contractors, status and PDF handover reports.",
        elements: [
          "Set up dwellings/lots, then record defects against an Area / Element / Issue (these pick-lists are private to each site manager).",
          "Each defect: description, photos, responsible contractor, status (open → in progress → closed).",
          "Issue contractors a secure link to view their defects and upload completion photos (no login needed).",
          "Generate Defect Handover PDF reports and contractor attendance reports.",
        ],
        gating: "Company Owner, Site Manager AND Supervisor can create/edit/close defects (defects are the one area Supervisors can modify). View-Only can view. Contractors use a public token link for their items only.",
      },
    ],
  },
  {
    group: "Clients",
    sections: [
      {
        id: "clients", title: "Client Management & Comms", img: "clients.png",
        intro: "Keep clients informed per site: send progress conversations, raise variations, and share photo updates — each delivered as a secure link the client opens without logging in, and can acknowledge.",
        elements: [
          "Conversations: send a client a message/update link; track when they view and acknowledge it.",
          "Variations: log scope variations and send them to the client for acknowledgement.",
          "Photo updates: share the day's client-visible site photos (see Site Photos).",
          "Each share is a unique token link; you can copy it, or send via WhatsApp/email. Links can be revoked.",
        ],
        gating: "Company Owner & Site Manager can create and send (Site Manager on assigned sites). Supervisor & View-Only can view. Clients use the public link only — they can view & acknowledge, nothing else.",
      },
    ],
  },
  {
    group: "OH&S (Health & Safety)",
    sections: [
      {
        id: "ohs-home", title: "OH&S Home", img: "ohs-home.png",
        intro: "The hub for site health & safety: daily attendance/sign-in, induction records and safety walks.",
        elements: ["Cards link to Daily Site Attendance, Site Induction Records, Safety Walks and Toolbox Talks.", "Each site has QR codes that workers scan on their phones to check in / complete inductions.", "The Toolbox Talks card flags any open corrective actions raised in a talk — click it to jump straight to them."],
        gating: "Company Owner & Site Manager manage OH&S (assigned sites for managers). Supervisor & View-Only view. Workers/visitors use public QR links — no account needed.",
      },
      {
        id: "ohs-attendance", title: "Daily Site Attendance", img: "ohs-attendance.png",
        intro: "Records who is on site each day via a QR self-check-in, building a daily attendance register per site. This is also where you generate the printable QR sign-in sheet for the site entrance.",
        elements: [
          "Workers scan the site QR, enter their details and check in — no login required.",
          "Managers see the day's attendance register and personnel directory (scoped to their sites).",
          "Generate the QR sheet: pick a site, click 'View QR Code', then choose Bilingual (English + 中文) or English only.",
          "Bilingual sheets add Simplified-Chinese instructions below the QR (heading, 'sign in daily', and a 'see the site manager if your company isn't listed' note) for non-English-speaking workforces.",
          "Download (PNG) or Print the sheet — Print opens an A4-sized print dialog ready to pin at the site entrance. The QR is permanent and never changes.",
          "Export the attendance register for records/audits.",
        ],
        gating: "Company Owner & Site Manager view/manage (assigned sites). Personnel are only visible to managers of sites where that person actually checked in. Supervisor/View-Only: view only. Public: QR check-in only.",
      },
      {
        id: "ohs-inductions", title: "Site Induction Records", img: "ohs-inductions.png",
        intro: "Captures site safety inductions: workers complete an induction form (via QR), with a record kept per site including signatures.",
        elements: ["Worker completes the induction form on their phone (site QR), including sign-off.", "Managers review who has been inducted for each site and keep the records for compliance."],
        gating: "Company Owner & Site Manager manage/view (assigned sites). Supervisor & View-Only view. Workers complete inductions via the public QR link.",
      },
      {
        id: "ohs-safety-walks", title: "Safety Walks", img: "ohs-safety-walks.png",
        intro: "Structured safety inspections ('walks') of a site, capturing findings, photos and actions, with a report per walk.",
        elements: ["Create a safety walk for a site; record observations, hazards, photos and corrective actions.", "Each walk is private to the site's manager / assigned sites; produce a report when done."],
        gating: "Company Owner & Site Manager create/edit (assigned sites). Supervisor & View-Only view. Safety walks are isolated per site.",
      },
      {
        id: "ohs-toolbox-talks", title: "Toolbox Talks", img: "ohs-toolbox-talks.png",
        intro: "Record a pre-start safety briefing: who was briefed, what was discussed and what came of it. Attendees auto-fill from that day's QR check-ins, everyone signs on the device, then you close the talk to lock it as a permanent record.",
        elements: [
          "Create a talk for a site/date and pick the TOPICS discussed (tap chips, or add your own).",
          "Add DISCUSSION NOTES (points raised by the Site Manager) and NEW BUSINESS (items raised by contractors) as numbered points on the talk screen.",
          "PERSONS PRESENT auto-fills from that day's QR attendance; add anyone missing with 'Add attendee'. Each person signs on-screen — unsigned people show 'Outstanding'.",
          "CORRECTIVE ACTION: raise one linked Task or Defect and assign it to someone on the talk (or yourself); its open/closed status mirrors the live record.",
          "CLOSE OUT locks the talk permanently and produces the PDF (record ID e.g. TBT-0048). Owners/Admins can reopen a locked talk to fix a genuine mistake.",
          "The OH&S Home card and the Toolbox list flag any OPEN corrective actions so follow-ups don't slip through — click the badge to jump straight to them.",
        ],
        gating: "Company Owner & Site Manager create / edit / close (assigned sites). Anyone except View-Only can sign. Only Owner/Admin can reopen a locked talk. Supervisor & View-Only can view and download the PDF.",
      },
    ],
  },
  {
    group: "Running your company",
    sections: [
      {
        id: "running-company", title: "Running your company", img: "home.png",
        intro: "If you're a Company Owner, Site Pro App is your own private workspace. Everything in your account — every site, every team member, every diary, defect, photo and client record — belongs to your company and is completely walled off from every other company on the platform. No other business can ever see your data, and you can never see theirs.",
        elements: [
          "Your company is your tenant: all sites and staff you create are tagged to your company automatically.",
          "Complete privacy: the 'company wall' filters every screen and report by your company first — there is no way for another company (or one of their staff) to reach your data, even with a direct link.",
          "As Owner you can do everything inside your company: create and delete sites, build your team, run every operational module, and view the Head-Office roll-up across all your sites.",
          "You can create staff logins for your team — Site Manager, Supervisor or View-Only.",
          "Soft plan limits (around 20 sites / 3 managers) are tracked but not blocked — you'll never be locked out mid-job.",
        ],
        gating: "As the Company Owner you have full control of your company. Site Managers, Supervisors and View-Only users only ever see the sites assigned to them.",
      },
      {
        id: "manage-team", title: "Manage Team", img: "site-managers.png",
        intro: "Manage Team (User Control) is where you build and run your own staff list — adding people, choosing what each can do, and controlling which sites they can reach. You self-serve this entirely.",
        elements: [
          "Add a team member: enter their name + email and pick a role — Site Manager, Supervisor or View-Only.",
          "Assign sites: tick which of your sites each person can access. Non-owners only ever see the sites you assign them — this is how you scope access.",
          "New staff receive a 'set your password' link; they choose their own password on first login.",
          "Reset a password or deactivate an account at any time if someone leaves or changes role.",
          "You create the three staff roles for your team — Site Manager, Supervisor and View-Only.",
        ],
        gating: "As Company Owner you manage your own team. Site Managers, Supervisors and View-Only cannot create or edit users.",
      },
      {
        id: "head-office-rollup", title: "Head-office roll-up", img: "overview.png",
        intro: "The Head-Office Overview is your single command-centre across every site in your company. Instead of opening sites one at a time, the roll-up totals the numbers that matter — open defects, outstanding tasks and recent diary activity — and surfaces the sites that need attention, so you can run the whole business from one screen.",
        elements: [
          "Company-wide totals: open vs closed defects, outstanding/overdue tasks, and recent site-diary activity summed across all your sites.",
          "'Needs attention' list: sites with the most open defects or overdue tasks float to the top so nothing slips through.",
          "Per-site breakdown: drill from the company total into any individual site to act on it.",
          "Always scoped to your company — your roll-up only ever includes your own sites.",
          "Open it any time from the menu — it's read-only reporting, so it's safe to share the screen in a head-office meeting.",
        ],
        gating: "The roll-up covers all of your company's sites. Site Managers, Supervisors and View-Only do not have the roll-up (they work at the individual-site level).",
      },
    ],
  },
  {
    group: "Company Settings",
    sections: [
      {
        id: "sites", title: "Sites", img: "sites.png",
        intro: "Create and manage your construction sites/projects, including site details and client info used across the app.",
        elements: ["Add/edit sites: name, address, client name/phone/email.", "Sites created here are what everything else (diary, defects, photos, OH&S) attaches to."],
        gating: "Company Owner can create/edit/delete sites. Site Managers can edit their assigned sites. Supervisors can view; View-Only have no access.",
      },
      {
        id: "site-managers", title: "User Control", img: "site-managers.png",
        intro: "Create and manage your team's user accounts, set their role, assign which sites each user can access, and deactivate people who leave.",
        elements: [
          "Create users and set role: Site Manager, Supervisor or View-Only.",
          "Assign one or more sites to each user — this is what scopes their access.",
          "Edit a user to change their role in place (no delete/recreate) and set a Position / Title; the card shows which Company the user belongs to.",
          "Role history: every role change is logged (who changed it, from → to, when) and viewable from the edit dialog.",
          "Deactivate / reactivate accounts: use the 'Show deactivated' toggle to reveal inactive users. Deactivated users are blocked from logging in until reactivated. A company's last active Owner cannot be deactivated.",
          "Reset a user's password at any time.",
        ],
        gating: "Company Owner can create/edit/deactivate users for their own team. Platform Admin can manage users across companies. Supervisors can view. Non-admins cannot grant the Admin or Company Owner role.",
      },
      {
        id: "manage-lists", title: "Manage Lists", img: "manage-lists.png",
        intro: "Maintain shared configuration lists used in dropdowns — trades and companies/contractors.",
        elements: ["Add/edit trades and companies used across diary, contractor and defect screens.", "Note: each Site Manager also keeps their own private trade/company/personnel lists, isolated from other managers."],
        gating: "Company Owner can edit. Supervisors can view. Site Managers & View-Only have no access here.",
      },
      {
        id: "company-profile", title: "Company Profile", img: "company-profile.png",
        intro: "Your company's own details and logo. What you enter here brands the documents your clients receive (e.g. the Variation Record), so it pays to complete it.",
        elements: [
          "Edit your company name, ABN, licence number, contact email/phone, website and business address.",
          "Upload your company logo (PNG/JPG/WEBP, up to 2MB) — it appears on client-facing documents.",
          "The profile is marked 'complete' once you have a name plus an email or phone.",
          "Each company's profile is private to that company.",
        ],
        gating: "Company Owner (and Platform Admin) can view and edit their own company profile. Other roles have no access.",
      },
    ],
  },
  {
    group: "Platform (operator only)",
    admin: true,
    sections: [
      {
        id: "admin-companies", title: "Companies & Admin Controls", img: "subscriptions.png",
        intro: "The operator's command-centre for every tenant company on the platform — owners, suspension/lockout, and full data backup, purge and restore. Reached from Controls → Companies (Platform Admin only).",
        elements: [
          "Company cards: see each company's status, owners and record counts at a glance.",
          "Multiple owners: promote or remove Company Owners (a company must always keep at least one active owner).",
          "Suspend / lock a company: with a reason — suspended companies are blocked at login AND on every request, and all their public links (defects, client variations, photo updates) stop working until reactivated. Admins are exempt.",
          "Reactivate restores access instantly. Suspend/reactivate auto-emails the company owners.",
          "Backup: take a non-destructive ZIP snapshot of a company (all data + media) and download it any time.",
          "Purge: permanently delete a company's data behind an exact-name type-to-confirm. A fresh backup is taken automatically right before purge and a secure download link is emailed to you as a safety net.",
          "Restore: upload a backup ZIP to recreate the company as a new tenant ('(Restored)').",
        ],
        gating: "Platform operator only. Company Owners and all other roles cannot reach this screen.",
      },
      {
        id: "demo-admin", title: "Demo & Trials", img: "demo-admin.png",
        intro: "Review and approve free-trial requests, and manage the demo/onboarding lifecycle.",
        elements: ["See incoming trial requests; approve to create a 30-day, single-site demo for the prospect.", "Share the customer's set-password link (copy or 1-tap WhatsApp)."],
        gating: "Platform operator only.",
      },
      {
        id: "subscriptions", title: "Subscriptions", img: "subscriptions.png",
        intro: "Manage paid plans, billing and subscribers (Stripe), plus anonymous visitor-traffic analytics.",
        elements: [
          "View subscribers and plan/billing status; payments are handled via Stripe checkout.",
          "Price Tiers: create versioned price tiers (existing subscribers are grandfathered) and set the current one.",
          "Notify & Migrate: move subscribers between tiers with at least 30 days' notice.",
          "Visitor Traffic tab: anonymous top-of-funnel stats — Homepage Visits, Pricing Views, Converted and Conversion Rate — by day/week/month (logged-in users excluded).",
        ],
        gating: "Platform operator only.",
      },
      {
        id: "platform-branding", title: "Platform Branding", img: "platform-branding.png",
        intro: "Operator-level branding and the support details shown to every customer. Reached from Controls → Platform Branding (Platform Admin only).",
        elements: [
          "Set the platform name and logo.",
          "Set the support email, support phone, support hours and call-back SLA.",
          "These support details power the tiered Contact Support screen every user sees.",
        ],
        gating: "Platform operator only.",
      },
    ],
  },
];

function Section({ s, role, forceOpen }) {
  const [open, setOpen] = useState(!!forceOpen);
  const ref = useRef(null);
  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      const t = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
      return () => clearTimeout(t);
    }
  }, [forceOpen]);
  const showBadge = role !== "admin" && !INFO_SECTIONS.includes(s.id);
  const fullAccess = canEdit(s.id, role);
  return (
    <div ref={ref} className="border border-slate-200 rounded-xl overflow-hidden bg-white scroll-mt-4" data-testid={`manual-section-${s.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50">
        <span className="flex items-center gap-2">
          <span className="font-semibold text-slate-800">{s.title}</span>
          {showBadge && (
            <Badge className={fullAccess ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
              {fullAccess ? "Full access" : "View only"}
            </Badge>
          )}
        </span>
        {open ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-5 pt-1 space-y-4">
          <p className="text-sm text-slate-700">{s.intro}</p>
          <img src={IMG(s.img)} alt={s.title} loading="lazy" className="w-full rounded-lg border border-slate-200 shadow-sm" />
          <div>
            <h4 className="text-sm font-semibold text-slate-800 mb-1">Key elements &amp; what they mean</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
              {s.elements.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
            <Lock className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
            <div>
              <span className="text-xs font-bold text-amber-800 uppercase tracking-wide">Who can do what</span>
              <p className="text-sm text-amber-900 mt-0.5">{s.gating}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CollapsibleCard({ title, icon: Icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="mb-6">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-5 text-left"
        data-testid={`collapsible-${title.replace(/\s+/g, "-").toLowerCase()}`}
      >
        <span className="flex items-center gap-2">
          {Icon && <Icon className="w-5 h-5 text-slate-700" />}
          <span className="text-lg font-bold text-slate-800">{title}</span>
        </span>
        {open ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
      </button>
      {open && <CardContent className="px-5 pb-5 pt-0">{children}</CardContent>}
    </Card>
  );
}

export default function Help() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const target = new URLSearchParams(location.search).get("section");
  // After a "?" deep-link opens its section, strip the param so any refresh
  // reloads the manual fully collapsed.
  useEffect(() => {
    if (target) window.history.replaceState({}, "", "/help");
  }, []);
  const role = user?.role || "view_only";
  const isAdmin = role === "admin";
  const isCompanyOwner = role === "company_owner";
  const showRolesOverview = isAdmin || isCompanyOwner;
  // Company Owners see a company-scoped roles list & matrix — no upstream Platform Admin column/rows.
  const ROLE_COL = { admin: 1, company_owner: 2, site_manager: 3, supervisor: 4, view_only: 5 };
  const PLATFORM_ROW_LABELS = ["Platform: Demo & Trials / all Subscribers", "Print / export the User Manual (PDF)"];
  const rolesShown = isAdmin ? ROLES : ROLES.filter((r) => r.key !== "admin");
  const permsShown = isAdmin ? PERMISSIONS : PERMISSIONS.filter((r) => !PLATFORM_ROW_LABELS.includes(r[0]));
  const [downloading, setDownloading] = useState(false);

  const downloadManual = async (variant) => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BACKEND_URL}/api/manual/pdf?variant=${variant}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to generate PDF");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SiteProApp-User-Manual-${variant === "company" ? "Company" : "Admin"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download the manual. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const isDemo = user?.is_demo && !user?.demo_converted;
  const myRole = ROLES.find((r) => r.key === role);

  const visibleGroups = GROUPS
    .map((g) => ({ ...g, sections: g.sections.filter((s) => canView(s.id, role)) }))
    .filter((g) => g.sections.length > 0);

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="user-manual-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-slate-800 flex items-center justify-center">
          <BookOpen className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">SiteProApp — User Manual</h1>
          <p className="text-sm text-slate-500">A guide to the screens you can use, what each number/colour means, and what you can do.</p>
        </div>
        {isAdmin && (
          <div className="ml-auto flex items-center gap-2" data-testid="manual-export-group">
            <span className="text-xs text-slate-400 hidden md:inline">Export PDF:</span>
            <Button
              onClick={() => downloadManual("admin")}
              disabled={downloading}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="download-manual-pdf-button"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Admin (full)
            </Button>
            <Button
              onClick={() => downloadManual("company")}
              disabled={downloading}
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="download-manual-company-pdf-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Company
            </Button>
          </div>
        )}
      </div>

      {showRolesOverview ? (
        <>
          {/* Roles overview (Platform Admin + Company Owner) */}
          <CollapsibleCard title="Roles & access" icon={Users}>
            <div className="grid sm:grid-cols-2 gap-3">
                {rolesShown.map((r) => {
                  const Icon = r.icon;
                  return (
                    <div key={r.key} className="flex items-start gap-3 border border-slate-200 rounded-lg p-3">
                      <div className={`w-9 h-9 rounded-lg ${r.color} flex items-center justify-center shrink-0`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{r.label}</p>
                        <p className="text-xs text-slate-600">{r.blurb}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Plus <b>public/token users</b> (contractors, clients, site workers) who never log in — they only open a single secure
                link sent to them and can do nothing else.
              </p>
          </CollapsibleCard>

          {/* Permissions matrix (Admin only) */}
          <CollapsibleCard title="Roles & Permissions matrix" icon={ShieldCheck}>
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" data-testid="permissions-matrix">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left p-2 font-semibold text-slate-700">Capability</th>
                      {rolesShown.map((r) => <th key={r.key} className="p-2 text-center font-semibold text-slate-700 whitespace-nowrap">{r.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {permsShown.map((row, i) => (
                      <tr key={i} className="border-b border-slate-100 align-top">
                        <td className="p-2 text-slate-700">{row[0]}<div className="text-xs text-slate-400">{row[6]}</div></td>
                        {rolesShown.map((r, j) => <td key={j} className="p-2 text-center"><PermCell v={row[ROLE_COL[r.key]]} /></td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><PermCell v={Y} /> Allowed</span>
                <span className="inline-flex items-center gap-1"><PermCell v={N} /> Not allowed</span>
                <span className="inline-flex items-center gap-1"><PermCell v={P} /> Limited / conditional</span>
                <span>Site Managers, Supervisors &amp; View-Only are always limited to their <b>assigned sites</b>.</span>
              </div>
          </CollapsibleCard>
        </>
      ) : (
        /* Personalised access summary (non-admins) */
        <Card className="mb-8 border-amber-200">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg ${myRole?.color || "bg-slate-500"} flex items-center justify-center shrink-0`}>
                {myRole && <myRole.icon className="w-5 h-5 text-white" />}
              </div>
              <div>
                <p className="text-sm text-slate-500">Your role</p>
                <h2 className="text-lg font-bold text-slate-800">{ROLE_LABEL[role]}{isDemo ? " · Free Trial" : ""}</h2>
                <p className="text-sm text-slate-600 mt-1">{myRole?.blurb}</p>
                {isDemo && (
                  <p className="text-sm text-amber-800 mt-2 bg-amber-50 border border-amber-200 rounded p-2">
                    You're on a <b>free trial</b> — limited to one site and a trial period. This guide shows the features available to you.
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  This manual only shows the screens your role can use. Each section is tagged
                  <Badge className="mx-1 bg-green-100 text-green-700">Full access</Badge> or
                  <Badge className="mx-1 bg-slate-100 text-slate-600">View only</Badge>
                  so you know what you can change. You can access only your <b>assigned sites</b>.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {visibleGroups.map((g) => (
        <div key={g.group} className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-lg font-bold text-slate-800">{g.group}</h2>
            {g.admin && <Badge className="bg-rose-100 text-rose-700">Admin area</Badge>}
          </div>
          <div className="space-y-3">
            {g.sections.map((s) => <Section key={s.id} s={s} role={role} forceOpen={target === s.id} />)}
            {g.group === "Defects" && <DefectsFlowchart />}
          </div>
        </div>
      ))}

      <p className="text-center text-xs text-slate-400 mt-8">
        Screenshots use sample data from a demonstration company — never real customer data. What you see in the app depends on your role and assigned sites.
      </p>
    </div>
  );
}
